import type { ReactNode } from 'react';

/**
 * A small Markdown subset for the first-party legal pages (web/public/legal/*.md), rendered as
 * React elements (no innerHTML): `#`–`###` headings, paragraphs, `-`/`*` and `1.` lists,
 * `>` callouts, `---` rules, and inline **bold**, *italic*, `code` and [links](url).
 */
export type Block =
  | { kind: 'h'; level: 1 | 2 | 3; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'hr' };

export function parseMarkdown(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', text: para.join(' ') });
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(t);
    if (h) {
      flush();
      blocks.push({ kind: 'h', level: h[1].length as 1 | 2 | 3, text: h[2] });
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(t)) {
      flush();
      blocks.push({ kind: 'hr' });
      continue;
    }
    const li = /^([-*]|\d+\.)\s+(.*)$/.exec(t);
    if (li) {
      flush();
      const kind = li[1].endsWith('.') ? 'ol' : 'ul';
      const last = blocks.at(-1);
      // Indented continuation lines belong to the previous item.
      if (last && last.kind === kind) last.items.push(li[2]);
      else blocks.push({ kind, items: [li[2]] });
      continue;
    }
    if (t.startsWith('>')) {
      flush();
      const text = t.replace(/^>\s?/, '');
      const last = blocks.at(-1);
      if (last?.kind === 'quote') last.text += ` ${text}`;
      else blocks.push({ kind: 'quote', text });
      continue;
    }
    const last = blocks.at(-1);
    if (!para.length && /^\s+/.test(line) && last && (last.kind === 'ul' || last.kind === 'ol')) {
      last.items[last.items.length - 1] += ` ${t}`;
      continue;
    }
    para.push(t);
  }
  flush();
  return blocks;
}

/** Inline **bold**, *italic*, `code` and [text](url) (http(s), mailto and same-site paths only). */
export function inline(text: string, key = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let at = 0;
  let n = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > at) out.push(text.slice(at, m.index));
    const k = `${key}-${n++}`;
    if (m[1] != null) out.push(<strong key={k}>{inline(m[1], k)}</strong>);
    else if (m[2] != null) out.push(<em key={k}>{inline(m[2], k)}</em>);
    else if (m[3] != null) out.push(<code key={k}>{m[3]}</code>);
    else {
      const href = m[5];
      const safe = /^(https?:|mailto:|\/)/.test(href);
      out.push(
        safe ? (
          <a
            key={k}
            href={href}
            className="text-accent underline"
            {...(href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
          >
            {m[4]}
          </a>
        ) : (
          m[4]
        ),
      );
    }
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

export function Markdown({ source }: { source: string }) {
  return (
    <div className="flex flex-col gap-3">
      {parseMarkdown(source).map((b, i) => {
        const k = `b${i}`;
        switch (b.kind) {
          case 'h':
            return b.level === 1 ? (
              <h1 key={k} className="t-title mt-2">
                {inline(b.text, k)}
              </h1>
            ) : b.level === 2 ? (
              <h2 key={k} className="t-heading mt-4">
                {inline(b.text, k)}
              </h2>
            ) : (
              <h3 key={k} className="t-label mt-2 font-semibold">
                {inline(b.text, k)}
              </h3>
            );
          case 'p':
            return (
              <p key={k} className="t-body text-dim">
                {inline(b.text, k)}
              </p>
            );
          case 'ul':
          case 'ol': {
            const List = b.kind;
            return (
              <List
                key={k}
                className={`t-body flex flex-col gap-1 pl-5 text-dim ${b.kind === 'ul' ? 'list-disc' : 'list-decimal'}`}
              >
                {b.items.map((it, j) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static document order
                  <li key={j}>{inline(it, `${k}-${j}`)}</li>
                ))}
              </List>
            );
          }
          case 'quote':
            return (
              <div
                key={k}
                role="note"
                className="t-label rounded-md border border-[rgba(255,181,71,0.45)] bg-accent-soft p-3"
              >
                {inline(b.text, k)}
              </div>
            );
          default:
            return <hr key={k} className="border-border" />;
        }
      })}
    </div>
  );
}
